/**
 * Hub UI primitives. The hub is the one screen the PRD lets go bold (PRD 6), so
 * these live here rather than in src/components — module screens stay quiet.
 *
 * Accessibility rules baked in here, not left to each card:
 *   - every interactive thing is >= 44px tall and has a visible focus ring
 *   - status is never color alone: every tone carries a word and an icon
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type Tone = 'ok' | 'near' | 'over' | 'quiet';

const TONE_CLASS: Record<Tone, string> = {
  ok: 'bg-sage-soft text-sage',
  near: 'bg-honey-soft text-honey',
  over: 'bg-clay-soft text-clay',
  quiet: 'bg-surface-sunk text-ink-soft',
};

// ---------------------------------------------------------------- icons

export type IconName =
  | 'wallet'
  | 'shield'
  | 'pulse'
  | 'pot'
  | 'broom'
  | 'calendar'
  | 'pin'
  | 'key'
  | 'gear'
  | 'bell'
  | 'check'
  | 'alert'
  | 'arrow'
  | 'car'
  | 'coin';

const PATHS: Record<IconName, ReactNode> = {
  wallet: (
    <>
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7.5Z" />
      <path d="M3 8h15" />
      <circle cx="17" cy="13" r="1.4" />
    </>
  ),
  pulse: (
    <>
      <path d="M3 12h3.5l2-5 3.5 11 2.5-6h6.5" />
    </>
  ),
  pot: (
    <>
      <path d="M4 10h16v3a6 6 0 0 1-6 6h-4a6 6 0 0 1-6-6v-3Z" />
      <path d="M20 11h1.5a1.5 1.5 0 0 1 0 3H20M4 11H2.5a1.5 1.5 0 0 0 0 3H4" />
      <path d="M9 6.5c0-1 1-1.2 1-2.2M14 6.5c0-1 1-1.2 1-2.2" />
    </>
  ),
  broom: (
    <>
      <path d="M14 4 9.5 8.5" />
      <path d="m7 11 6-6 3 3-6 6z" />
      <path d="M9 13 4.5 20h11.2c1 0 1.6-1 1.2-1.9L15 14" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h9M18 12v3.5M15.5 12v2.5" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v2.2M12 19.3v2.2M4.2 7.2l1.9 1.1M17.9 15.7l1.9 1.1M4.2 16.8l1.9-1.1M17.9 8.3l1.9-1.1" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.2l7 2.5v5.6c0 4.2-2.9 7.6-7 8.9-4.1-1.3-7-4.7-7-8.9V5.7l7-2.5Z" />
      <path d="m9 12 2 2 4-4.2" />
    </>
  ),
  bell: (
    <>
      <path d="M6 9a6 6 0 1 1 12 0c0 3.3 1 5 1.8 6H4.2C5 14 6 12.3 6 9Z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.3 2.4 2.4 4.6-5" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.2M12 16.2v.6" />
    </>
  ),
  arrow: (
    <>
      <path d="M5 12h13M13 6.5 18.5 12 13 17.5" />
    </>
  ),
  car: (
    <>
      <path d="M4 16v2.5M20 16v2.5" />
      <path d="M3 15.5v-3l1.8-4A2 2 0 0 1 6.7 7h10.6a2 2 0 0 1 1.9 1.5l1.8 4v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z" />
      <path d="M4.5 12.5h15" />
    </>
  ),
  coin: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M14.5 9.3A2.8 2.8 0 0 0 12 8c-1.5 0-2.6.8-2.6 2s1 1.7 2.6 2 2.6.9 2.6 2-1.1 2-2.6 2a2.8 2.8 0 0 1-2.5-1.3M12 6.5v11" />
    </>
  ),
};

export function Icon({
  name,
  className = 'h-5 w-5',
}: {
  name: IconName;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

// ---------------------------------------------------------------- shell

export function Card({
  children,
  className = '',
  as: Tag = 'section',
}: {
  children: ReactNode;
  className?: string;
  as?: 'section' | 'div';
}) {
  return (
    <Tag
      className={`rounded-card border border-border bg-surface shadow-[0_1px_3px_rgba(43,39,35,0.05)] ${className}`}
    >
      {children}
    </Tag>
  );
}

export function CardTitle({
  icon,
  children,
  action,
}: {
  icon: IconName;
  children: ReactNode;
  action?: { to: string; label: string };
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold tracking-wide text-ink-soft uppercase">
        <Icon name={icon} className="h-4 w-4 text-ink-faint" />
        {children}
      </h2>
      {action && (
        <Link
          to={action.to}
          className="-mr-2 inline-flex min-h-11 items-center rounded-full px-2 text-sm font-medium text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}

/** Text + icon + color. Never color alone. */
export function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  const icon: IconName = tone === 'over' ? 'alert' : tone === 'near' ? 'alert' : 'check';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASS[tone]}`}
    >
      {tone !== 'quiet' && <Icon name={icon} className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
}

export function Skeleton({ className = 'h-4 w-full' }: { className?: string }) {
  return <div className={`animate-pulse rounded-full bg-surface-sunk ${className}`} />;
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3" role="status" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className={`h-4 ${i % 2 === 0 ? 'w-full' : 'w-2/3'}`} />
      ))}
    </div>
  );
}

/** Day-one is all empty states — so each one names the first useful action. */
export function Empty({
  message,
  action,
}: {
  message: string;
  action?: { to: string; label: string };
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-soft">{message}</p>
      {action && (
        <Link
          to={action.to}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-sage px-4 text-sm font-medium text-white transition-colors hover:bg-sage/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          {action.label}
          <Icon name="arrow" className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

export function ErrorState({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="space-y-3">
      <p className="flex items-start gap-2 text-sm text-clay">
        <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Couldn&rsquo;t load {what}.</span>
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex min-h-11 items-center rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        Try again
      </button>
    </div>
  );
}

/** A colored dot that carries a name — the dot is decoration, the text is the label. */
export function MemberDot({ color, name }: { color: string | null; name: string }) {
  const initial = name.slice(0, 1).toUpperCase();
  return (
    <span
      className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold text-white"
      style={{ backgroundColor: color ?? 'var(--color-ink-faint)' }}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}
